import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { SidebarService } from '@core/services/sidebar.service';
import { AuthService } from '@core/services/auth.service';
import { ApiService } from '@core/services/api.service';
import { CompanySettingsService } from '@core/services/company-settings.service';
import { PermissionService } from '@core/services/permission.service';
import { MENU_REGISTRY } from '@core/models/role-config.model';
import { RoleConfigService } from '@core/services/role-config.service';

@Component({
  standalone: false,
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {

  expanded: boolean = true;
  currentRoute: string = '';
  userRole: string = 'employee';
  userRoleLabel: string = 'Employee';
  showSystemMenu: boolean = false;
  companyLogo: string | null = null;
  companyName: string = '';
  private settingsSub: Subscription | null = null;

  private readonly sectionDefs = [
    { keys: ['dashboard', 'my_team'],                                                          label: 'Home',              short: 'Home',     railIcon: 'home',        divider: false },
    { keys: ['attendance', 'roster', 'timesheet', 'leave', 'holidays'],                        label: 'Time & Attendance', short: 'Time',     railIcon: 'clock',       divider: true  },
    { keys: ['employees', 'departments', 'onboarding', 'offboarding', 'performance', 'appraisal', 'asset_management'], label: 'Workforce', short: 'People',   railIcon: 'users',       divider: true },
    { keys: ['payroll', 'clients'],                                                            label: 'Payroll & Billing', short: 'Payroll',  railIcon: 'wallet',      divider: true  },
    { keys: ['reports'],                                                                        label: 'Insights',          short: 'Insights', railIcon: 'bar-chart',   divider: true  },
    { keys: ['master_data', 'settings', 'policies', 'migration', 'activity_log', 'geo_zones'], label: 'Administration', short: 'Admin',    railIcon: 'shield', divider: true  },
    { keys: ['my_payslips', 'change_requests', 'profile', 'announcements'],                      label: 'Account',           short: 'Account',  railIcon: 'user',        divider: true  },
  ];

  private readonly itemIcons: { [key: string]: string } = {
    dashboard: 'layout-dashboard', my_team: 'users',
    attendance: 'calendar-check', roster: 'calendar-days', timesheet: 'clock',
    leave: 'calendar-x', holidays: 'sun',
    employees: 'users', departments: 'git-branch', master_data: 'database', onboarding: 'user-plus',
    offboarding: 'log-out', performance: 'activity', appraisal: 'clipboard-check', asset_management: 'package',
    payroll: 'banknote', my_payslips: 'receipt', clients: 'briefcase',
    reports: 'bar-chart',
    settings: 'settings', roles: 'shield-check', policies: 'file-text',
    migration: 'database', activity_log: 'history', email_templates: 'mail', geo_zones: 'map-pin',
    announcements: 'megaphone', profile: 'user',
  };

  flatSections: { items: any[]; label: string; short: string; railIcon: string; divider: boolean; expanded: boolean }[] = [];

  openGroup: number | null = null;
  userFlyoutOpen = false;
  private flyoutTimer: any = null;

  readonly menuRegistry = MENU_REGISTRY;

  private readonly selfServiceLabels: { [key: string]: string } = {
    attendance: 'My Attendance',
    leave:      'My Leaves',
    dashboard:  'Dashboard',
  };

  mainMenuItems: any[] = [];
  adminMenuItems: any[] = [];
  accountMenuItems: any[] = [];

  mainLabel = 'MAIN MENU';
  adminLabel = 'ADMINISTRATION';

  userName: string = '';
  userEmail: string = '';
  userInitials: string = 'AD';
  userPhoto: string | null = null;

  constructor(
    private router: Router,
    private sidebarService: SidebarService,
    private authService: AuthService,
    private apiService: ApiService,
    private companySettingsService: CompanySettingsService,
    private permissionService: PermissionService,
    private roleConfigService: RoleConfigService
  ) {}

  ngOnInit(): void {
    this.sidebarService.subscribeToEvent((data: boolean) => {
      this.expanded = data;
    });
    this.currentRoute = this.router.url;
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => {
      this.currentRoute = e.urlAfterRedirects || e.url;
    });
    this.loadUserInfo();
    this.loadRolePermissions();
    this.loadCompanySettings();
    this.settingsSub = this.companySettingsService.settings$.subscribe(settings => {
      if (settings) {
        this.companyLogo = settings.logo || null;
        this.companyName = settings.companyName || '';
      }
    });
  }

  ngOnDestroy(): void {
    if (this.settingsSub) this.settingsSub.unsubscribe();
  }

  private loadCompanySettings(): void {
    this.apiService.get('/company-settings').subscribe({
      next: (data: any) => {
        if (data) {
          this.companyLogo = data.logo || null;
          this.companyName = data.companyName || '';
          this.companySettingsService.updateSettings(data);
        }
      },
      error: () => {}
    });
  }

  private static readonly ROLE_LABELS: { [k: string]: string } = {
    super_admin: 'Super Admin', admin: 'Administrator', hr_admin: 'HR Admin',
    hr: 'HR Manager', manager: 'Manager', employee: 'Employee', ceo: 'CEO',
    hr_attendance_manager: 'Attendance Manager',
  };

  private loadUserInfo(): void {
    const user = this.authService.getCurrentUser();
    if (user) {
      this.userName = (user.firstName || '') + ' ' + (user.lastName || '');
      this.userEmail = user.email || '';
      this.userInitials = ((user.firstName?.charAt(0) || '') + (user.lastName?.charAt(0) || '')).toUpperCase();
      this.userRole = user.role || 'employee';
      this.userRoleLabel = this.roleLabel(user.role);

      // Login payload doesn't carry the profile photo — fetch it once so the rail/flyout
      // avatar can show the real picture instead of always falling back to initials.
      if (user.id) {
        this.apiService.get('/employees/' + user.id).subscribe({
          next: (emp: any) => { if (emp?.profilePicture) this.userPhoto = emp.profilePicture; },
          error: () => {}
        });
      }
    }
  }

  roleLabel(role: string | null | undefined): string {
    const r = (role || '').toLowerCase().replace(/^role_/, '');
    if (!r) return 'Member';
    return SidebarComponent.ROLE_LABELS[r] || r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  private loadRolePermissions(): void {
    const cached = this.roleConfigService.getConfig();
    this.applyMenuPermissions(cached.allowedMenuKeys);

    this.roleConfigService.load(this.userRole).subscribe({
      next: (config) => {
        this.applyMenuPermissions(config.allowedMenuKeys);
      },
      error: () => {
      }
    });
  }

  private applyRoleConfigMenus(): void {
    const config = this.roleConfigService.getConfig();
    this.mainLabel = config.mainLabel;
    this.adminLabel = config.adminLabel;
    this.applyMenuPermissions(config.allowedMenuKeys);
  }

  private buildSections(): void {
    const allAllowed = [...this.mainMenuItems, ...this.adminMenuItems, ...this.accountMenuItems];
    const currentRoute = this.currentRoute;
    this.flatSections = this.sectionDefs
      .map(s => {
        const items = s.keys.map(k => allAllowed.find(m => m.key === k)).filter(Boolean);
        const hasActive = items.some((item: any) => currentRoute.startsWith(item.route));
        return { items, label: s.label, short: s.short, railIcon: s.railIcon, divider: s.divider, expanded: !s.label || hasActive };
      })
      .filter(s => s.items.length > 0);
  }

  private applyMenuPermissions(serverAllowedMenus: string[]): void {
    const base = serverAllowedMenus.length > 0 ? serverAllowedMenus : this.roleConfigService.getConfig().allowedMenuKeys;
    const r = (this.userRole || '').toLowerCase().replace(/^role_/, '');

    const forced = ['timesheet', 'my_team', 'appraisal'];

    if (['hr', 'hr_admin', 'admin', 'super_admin'].includes(r)) {
      forced.push('roster', 'clients', 'departments', 'master_data');
    }
    if (['hr_admin', 'admin', 'super_admin'].includes(r)) {
      forced.push('policies', 'migration', 'activity_log');
    }
    if (base.includes('teams')) forced.push('departments');

    const allowed = [...new Set([...base, ...forced])];
    this.mainMenuItems    = this.menuRegistry.filter(m => m.section === 'main'    && allowed.includes(m.key));
    this.adminMenuItems   = this.menuRegistry.filter(m => m.section === 'admin'   && allowed.includes(m.key));
    this.accountMenuItems = this.menuRegistry.filter(m => m.section === 'account' && allowed.includes(m.key));
    this.buildSections();
  }

  getDisplayLabel(item: any): string {
    if (this.userRole === 'employee' || this.userRole === 'role_employee') {
      return this.selfServiceLabels[item.key] ?? item.label;
    }
    return item.label;
  }

  toggleSection(section: any): void {
    section.expanded = !section.expanded;
  }

  navigate(route: string): void {
    this.router.navigate([route]);
    this.currentRoute = route;
    this.closeFlyoutNow();
  }

  isActive(route: string): boolean {
    return this.currentRoute.startsWith(route);
  }

  iconFor(item: any): string {
    return this.itemIcons[item.key] || 'circle';
  }

  isGroupActive(section: any): boolean {
    return section.items.some((item: any) => this.isActive(item.route));
  }

  selectGroup(section: any): void {
    if (section.items.length) this.navigate(section.items[0].route);
  }

  isTouchLike(): boolean {
    try { return window.matchMedia('(hover: none), (max-width: 768px)').matches; }
    catch { return false; }
  }

  onRailClick(section: any, index: number): void {
    if (this.isTouchLike() && section.items.length > 1) {
      this.userFlyoutOpen = false;
      this.openGroup = this.openGroup === index ? null : index;
      return;
    }
    this.selectGroup(section);
  }

  onAvatarClick(): void {
    if (this.isTouchLike()) {
      this.userFlyoutOpen = !this.userFlyoutOpen;
      this.openGroup = null;
      return;
    }
    this.navigate('/profile');
  }

  openFlyout(index: number): void {
    if (this.flyoutTimer) { clearTimeout(this.flyoutTimer); this.flyoutTimer = null; }
    this.userFlyoutOpen = false;
    this.openGroup = index;
  }

  openUserFlyout(): void {
    if (this.flyoutTimer) { clearTimeout(this.flyoutTimer); this.flyoutTimer = null; }
    this.openGroup = null;
    this.userFlyoutOpen = true;
  }

  scheduleClose(): void {
    if (this.flyoutTimer) clearTimeout(this.flyoutTimer);
    this.flyoutTimer = setTimeout(() => {
      this.openGroup = null;
      this.userFlyoutOpen = false;
      this.flyoutTimer = null;
    }, 150);
  }

  cancelClose(): void {
    if (this.flyoutTimer) { clearTimeout(this.flyoutTimer); this.flyoutTimer = null; }
  }

  closeFlyoutNow(): void {
    if (this.flyoutTimer) { clearTimeout(this.flyoutTimer); this.flyoutTimer = null; }
    this.openGroup = null;
    this.userFlyoutOpen = false;
  }

  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  logout(): void {
    this.authService.logout();
  }
}
